import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { RestaurantProvider, useRestaurant } from './context/RestaurantContext';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppSettingsProvider } from './context/AppSettingsContext';
import { LanguageProvider, useT } from './context/LanguageContext';
import ErrorBoundary from './components/ErrorBoundary';
import { Toaster } from './components/ui/use-toast';
import { ConfirmDialogHost } from './components/ui/confirm-dialog';
import ThemeQuickSwitch from './components/ThemeQuickSwitch';
import LanguageQuickSwitch from './components/LanguageQuickSwitch';
import NotificationBell from './components/NotificationBell';
import MenuSearch from './components/MenuSearch';
import { HeaderHelpButton, FullscreenToggle } from './components/HeaderIconButtons';
import HeaderUserChip from './components/HeaderUserChip';
import Breadcrumbs from './components/Breadcrumbs';
import { PanelLeftOpen, MapPin, Clock, RefreshCw } from 'lucide-react';
import Sidebar from './components/Sidebar';
import DodoHelp from './components/DodoHelp';
import ModernLogin from './pages/ModernLogin';
import SsoLanding from './pages/SsoLanding';
import AppSwitcher from './components/AppSwitcher';
import FirstTimeOnboarding from './components/FirstTimeOnboarding';
import WelcomeTipsToast from './components/WelcomeTipsToast';
import ReservationRequestsWatcher from './components/ReservationRequestsWatcher';
import { playLoginChime } from './utils/loginChime';
import './styles/App.css';
import WelcomeScreen from './pages/WelcomeScreen';
import Dashboard from './pages/Dashboard';
import Reports from './pages/Reports';
import ReportView from './pages/reports/ReportView';
import AddEditDish from './pages/AddEditDish';
import AllDishesList from './components/menu/AllDishesList';
import MenuManagementParent from './pages/MenuManagementParent';
import OrderManagement from './pages/OrderManagement';
import KitchenView from './pages/kitchen/KitchenView';
import ViewMenu from './pages/ViewMenu';
// New simplified menu pages
import MenuList from './pages/menu/MenuList';
import MenuForm from './pages/menu/MenuForm';
import MenuView from './pages/menu/MenuView';
import MenuDesignFactory from './pages/menu/MenuDesignFactory';
import MenuLocationPicker from './pages/menu/MenuLocationPicker';
import BuffetManagement from './pages/buffet/BuffetManagement';
import BuffetInsights from './pages/buffet/BuffetInsights';
import OpenBuffets from './pages/buffet/OpenBuffets';
import LiveCountersPage from './pages/buffet/LiveCountersPage';
import BuffetDishQuantities from './pages/buffet/BuffetDishQuantities';
import BuffetPlanner from './pages/buffet/BuffetPlanner';
import BuffetPlans from './pages/buffet/BuffetPlans';
// Tables Management
import TablesViewPage from './pages/tables/TablesViewPage';
import ReservationPage from './pages/ReservationPage';
import AllReservations from './pages/AllReservations';
import ReservationInsights from './pages/reservations/ReservationInsights';
import ReservationLink from './pages/reservations/ReservationLink';
// Staff Management and User Tracking components
import StaffManagement from './components/StaffManagement';
import CustomersPage from './pages/customers/CustomersPage';
import CrmPage from './pages/crm/CrmPage';
import LoyaltyManager from './pages/loyalty/LoyaltyManager';
import MembersPage from './pages/loyalty/MembersPage';
import MemberProfile from './pages/loyalty/MemberProfile';
import RewardsCataloguePage from './pages/loyalty/RewardsCataloguePage';
import GiftCardsPage from './pages/loyalty/GiftCardsPage';
import LoyaltyProgramSettings from './pages/loyalty/LoyaltyProgramSettings';
import CustomerProfilePage from './pages/crm/CustomerProfilePage';
import ForgotPassword from './pages/ForgotPassword';
import CustomerAccountLogin from './pages/customer/CustomerAccountLogin';
import UserSessions from './components/UserSessions';
import SessionDetails from './pages/SessionDetails';
import SettingsDisplay from './pages/SettingsDisplay';
import MastersManagement from './pages/MastersManagement';
import AppPreferences from './pages/AppPreferences';
import MenuViewSettings from './pages/MenuViewSettings';
import CompanyProfile from './pages/CompanyProfile';
import OrganisationSetup from './pages/OrganisationSetup';
import LocationsManagement from './pages/LocationsManagement';
import RestaurantGroupsManagement from './pages/RestaurantGroupsManagement';
import WebsiteSettings from './pages/WebsiteSettings';
import KioskSettings from './pages/KioskSettings';
import IngredientsManagement from './pages/IngredientsManagement';
import ReservationSettingsPage from './pages/ReservationSettingsPage';
import NotificationSettings from './pages/NotificationSettings';
import RealtimePage from './pages/RealtimePage';
import DevicesManagement from './pages/DevicesManagement';
import DeviceShiftsReport from './pages/DeviceShiftsReport';
import PurchaseLocationPicker from './pages/purchase/PurchaseLocationPicker';
import PurchaseHome from './pages/purchase/PurchaseHome';
import NewPurchase from './pages/purchase/NewPurchase';
import PurchaseDetail from './pages/purchase/PurchaseDetail';
import InventoryLocationPicker from './pages/inventory/InventoryLocationPicker';
import InventoryManagement from './pages/inventory/InventoryManagement';
import BarInventoryLocationPicker from './pages/bar-inventory/BarInventoryLocationPicker';
import BarInventoryManagement from './pages/bar-inventory/BarInventoryManagement';
import LiquorManagementLocationPicker from './pages/liquor-management/LiquorManagementLocationPicker';
import LiquorManagement from './pages/liquor-management/LiquorManagement';
import LiquorTrackingLocationPicker from './pages/liquor-tracking/LiquorTrackingLocationPicker';
import LiquorTrackingHome from './pages/liquor-tracking/LiquorTrackingHome';
import LiquorSets from './pages/liquor-tracking/LiquorSets';
import StocktakeWorkspace from './pages/liquor-tracking/StocktakeWorkspace';
import StocktakeReport from './pages/liquor-tracking/StocktakeReport';
import TaxRatesManagement from './pages/TaxRatesManagement';
import TaxGroupsManagement from './pages/TaxGroupsManagement';
import TaxMigrationPage from './pages/TaxMigrationPage';
import StaffCategories from './pages/StaffCategories';
import RolesManagement from './pages/RolesManagement';
import StaffDetail from './pages/StaffDetail';
import RequirePermission from './components/RequirePermission';
import QRCodes from './pages/QRCodes';
import HelpCenter from './pages/HelpCenter';
import CustomerLogin from './pages/customer/CustomerLogin';
import CustomerReserve from './pages/customer/CustomerReserve';
import CustomerMenu from './pages/customer/CustomerMenu';
import KioskOrder from './pages/customer/KioskOrder';
import { isBareDomain } from './lib/bareDomain';

// Only an address that names no restaurant ever downloads the landing page.
const RestoLanding = lazy(() => import('./pages/RestoLanding'));

/* Code-split: these two pull in recharts, which nothing else uses. Kept out
   of the main bundle so opening the kitchen board never downloads a
   charting library. */
const InsightsHub = lazy(() => import('./pages/selfservice/insights/InsightsHub'));
const FestivalCalendar = lazy(() => import('./pages/selfservice/insights/FestivalCalendar'));
const InsightPlaceholder = lazy(() => import('./pages/selfservice/insights/InsightPlaceholder'));

// The active location chip and the brand slot, which falls back to the product name until the company resolves.
function HeaderBrand() {
  const { restaurant } = useRestaurant();
  const { t } = useT();
  return (
    <h1 className="text-xl font-semibold truncate max-w-[16rem]" title={restaurant?.name || t('common.appName')}>
      {restaurant?.name || t('common.appName')}
    </h1>
  );
}

function HeaderLocation() {
  const { location } = useRestaurant();
  const { t } = useT();
  if (!location?.name) return null;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full bg-accent px-2.5 py-1 text-xs font-medium text-accent-foreground"
      title={t('common.currentLocation')}
    >
      <MapPin size={13} className="text-primary" />
      {location.name}
    </span>
  );
}

// The top bar's wall clock, ticking at minute precision - a till doesn't need seconds.
function HeaderClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 15000);
    return () => clearInterval(t);
  }, []);
  return (
    <span
      className="hidden md:inline-flex items-center gap-1.5 text-sm text-muted-foreground tabular-nums whitespace-nowrap"
      title={now.toLocaleString()}
    >
      <Clock size={14} className="text-primary" />
      {now.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' })}
      <span className="font-medium text-foreground">
        {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </span>
    </span>
  );
}

/* When the running build was deployed; a later read coming back newer turns the stamp into a "refresh" button.
 *
 * Two places show it. The bar carries it where it can be READ without opening
 * anything — "did my deploy land?" is asked while looking at the screen, not
 * while hunting through a menu — and the user menu keeps a copy for the narrow
 * screens the bar pill drops off. "A newer build is out" is an ALERT and stays
 * on the bar alone (`variant="bar"`): burying a prompt to reload behind a click
 * is how a stale tab stays stale all evening. */
function BuildStamp({ variant = 'bar' }) {
  const [builtAt, setBuiltAt] = useState(null);
  const [staleSince, setStaleSince] = useState(null);
  const firstSeen = React.useRef(null);

  useEffect(() => {
    let alive = true;
    const pull = () => fetch('/build-info.json', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!alive || !j?.built_at) return;
        if (!firstSeen.current) {
          firstSeen.current = j.built_at;
          setBuiltAt(j.built_at);
        } else if (j.built_at !== firstSeen.current) {
          setStaleSince(j.built_at);
        }
      })
      .catch(() => { /* dev server / offline — show nothing rather than lie */ });
    pull();
    const t = setInterval(pull, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (staleSince) {
    // The alert belongs on the bar; the menu copy stays quiet about it.
    if (variant === 'menu') return null;
    return (
      <button
        type="button"
        onClick={() => window.location.reload()}
        title={`A newer build was deployed at ${new Date(staleSince).toLocaleString()} — reload to get it`}
        className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100"
      >
        <RefreshCw size={12} /> New build — refresh
      </button>
    );
  }
  // Nothing to alert about — both places print the plain stamp.
  if (!builtAt) return null;
  const d = new Date(builtAt);
  return (
    <span
      className={
        variant === 'bar'
          /* A quiet pill beside the clock. It steps off narrow screens rather
             than crowding the search — the user menu still has the stamp. */
          ? 'hidden lg:inline-flex items-center gap-1 rounded-full border border-border '
            + 'bg-muted/40 px-2.5 py-1 text-[11px] text-muted-foreground whitespace-nowrap'
          : 'inline-flex items-center gap-1 text-[11px] text-muted-foreground whitespace-nowrap'
      }
      title={`This build was deployed ${d.toLocaleString()}`}
    >
      build {d.toLocaleDateString([], { day: 'numeric', month: 'short' })}{' '}
      {d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
    </span>
  );
}

// Main authenticated app component
function AuthenticatedApp() {
  const { logout, user } = useAuth();
  const { t } = useT();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  // The router's path, not the browser global: it is what re-keys the error
  // boundary, so it has to change when a client-side navigation does.
  const location = useLocation();

  return (
    <RestaurantProvider>
      <AppSettingsProvider>
        <div className="App flex flex-col h-screen overflow-hidden">
        <div className="flex flex-1 min-h-0">
          {/* Sidebar */}
          {/* Collapsed it becomes an icon rail rather than disappearing: the
              modules stay one click away, which is the point of a sidebar. */}
          <Sidebar
            collapsed={!sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            onOpen={() => setSidebarOpen(true)}
          />

          {/* Main content */}
          <div className="flex-1 min-w-0 bg-background flex flex-col min-h-0">
            {/* Header with logout - h-14 matches the sidebar brand bar so the two header borders line up. */}
            <div className="bg-card shadow-sm border-b px-4 h-14 flex justify-between items-center gap-4 shrink-0">
              {/* min-w-0 so this half yields to the icons: the trail truncates
                  rather than shoving the search off the row. */}
              <div className="flex items-center gap-3 min-w-0">
                {!sidebarOpen && (
                  <button
                    type="button"
                    onClick={() => setSidebarOpen(true)}
                    aria-label={t('common.showSidebar')}
                    title={t('common.showSidebar')}
                    className="shrink-0 p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                  >
                    <PanelLeftOpen size={18} />
                  </button>
                )}
                <HeaderBrand />
                <HeaderLocation />
                {/* Where you are, ON the header row rather than a strip of its
                    own beneath it — the trail is a few words, and a whole band
                    of chrome to carry them cost more height than they are
                    worth. The rule stands in for the row it replaced. */}
                <span className="hidden md:block h-5 w-px shrink-0 bg-border" aria-hidden="true" />
                <Breadcrumbs inline />
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {/* Type-to-find for the whole menu, reading the sidebar's own
                    tree — first in this group so it sits beside the icons the
                    way every search box of this shape does. */}
                <MenuSearch />
                {/* Only ever the "a newer build is out" alert; the quiet stamp
                    itself now sits at the foot of the user menu. */}
                <BuildStamp variant="bar" />
                <HeaderClock />
                <AppSwitcher currentProductSlug="restoboxx" />
                <HeaderHelpButton />
                <NotificationBell />
                <LanguageQuickSwitch />
                <ThemeQuickSwitch />
                <FullscreenToggle />
                {/* Who is signed in, last on the row: a bare initials avatar
                    whose menu carries the name, the role, preferences, which
                    outlet you are reading and the way out. */}
                <HeaderUserChip
                  user={user}
                  onLogout={logout}
                  logoutLabel={t('common.logout')}
                  footer={<BuildStamp variant="menu" />}
                />
              </div>
            </div>

            {/* pb-28 reserves a safe area so bottom-right page actions never sit under the floating help FAB. */}
            <div className="flex-1 overflow-auto min-h-0 pb-28">
            {/* One screen throwing while it renders used to unmount the whole
                app and leave a white page. The boundary sits INSIDE the shell,
                so the sidebar and header survive, and it is keyed on the path
                so walking away from a broken screen clears it. */}
            <ErrorBoundary resetKey={location.pathname}>
            <Routes>
            <Route path="/" element={<WelcomeScreen />} />
            <Route path="/login" element={<ModernLogin />} />
            <Route
              path="/dashboard"
              element={<Dashboard />}
            />
            <Route path="/reports" element={<Reports />} />
            <Route path="/reports/:reportKey" element={<ReportView />} />

              {/* RestoBOXX-InTutor — full-platform manual, tutorials & glossary */}
              <Route path="/help" element={<HelpCenter />} />
              <Route path="/help/:topicId" element={<HelpCenter />} />

              <Route path="/restaurant/:restaurantId/dishes" element={<AllDishesList />} />
              <Route path="/dishes" element={<AllDishesList />} />
              <Route path="/restaurant/:restaurantId/dish/new" element={<AddEditDish />} />
              <Route path="/restaurant/:restaurantId/dish/add" element={<AddEditDish />} />
              <Route path="/restaurant/:restaurantId/dish/:dishId/edit" element={<AddEditDish />} />
              
              {/* Menu Management Routes */}
              <Route path="/menu-management" element={<MenuManagementParent />} />
              <Route path="/order" element={<RequirePermission cap="can_handle_payments"><OrderManagement /></RequirePermission>} />
              <Route path="/orders/active" element={<RequirePermission cap="can_handle_payments"><OrderManagement activeTab="active" /></RequirePermission>} />
              <Route path="/orders/all" element={<RequirePermission cap="can_handle_payments"><OrderManagement activeTab="list" /></RequirePermission>} />
              {/* Kitchen View (KDS): deliberately not behind can_handle_payments, since kitchen staff don't take payments. */}
              <Route path="/kitchen" element={<KitchenView />} />
              <Route path="/menu/:menuId/view" element={<ViewMenu />} />
              
              {/* Open Buffet Management — buffets + dynamic pricing per branch */}
              <Route path="/restaurant/:restaurantId/buffets" element={<BuffetManagement />} />
              <Route path="/buffets" element={<BuffetManagement />} />
              {/* Open Buffets, declared BEFORE /buffets/:anything so "open" is never taken for a parameter. */}
              <Route path="/restaurant/:restaurantId/buffets/open" element={<OpenBuffets />} />
              <Route path="/buffets/open" element={<OpenBuffets />} />
              {/* The live-counter REGISTER, reachable from the Buffet group; the same panel also lives in Settings → Master Data. */}
              <Route path="/restaurant/:restaurantId/buffets/live-counters" element={<LiveCountersPage />} />
              <Route path="/buffets/live-counters" element={<LiveCountersPage />} />
              <Route path="/restaurant/:restaurantId/buffets/dish-quantities" element={<BuffetDishQuantities />} />
              <Route path="/buffets/dish-quantities" element={<BuffetDishQuantities />} />
              {/* Buffet planning: the resolved calendar for one plan. Every
                  day on it is computed from the rule stack, never stored. */}
              <Route path="/restaurant/:restaurantId/buffet-plans/:planId/calendar" element={<BuffetPlanner />} />
              <Route path="/buffet-plans/:planId/calendar" element={<BuffetPlanner />} />
              <Route path="/restaurant/:restaurantId/buffet-plans" element={<BuffetPlans />} />
              <Route path="/buffet-plans" element={<BuffetPlans />} />
              <Route path="/restaurant/:restaurantId/buffets/insights" element={<BuffetInsights />} />
              <Route path="/buffets/insights" element={<BuffetInsights />} />
              <Route path="/restaurant/:restaurantId/buffets/insights/:report" element={<BuffetInsights />} />
              <Route path="/buffets/insights/:report" element={<BuffetInsights />} />

              {/* Menus are location-scoped: the bare /menus path is a picker, the CRUD lives under /locations/:locationId/menus. */}
              <Route path="/restaurant/:restaurantId/menus" element={<MenuLocationPicker />} />
              <Route path="/restaurant/:restaurantId/locations/:locationId/menus" element={<MenuList />} />
              <Route path="/restaurant/:restaurantId/locations/:locationId/menus/new" element={<MenuForm />} />
              <Route path="/restaurant/:restaurantId/locations/:locationId/menus/:menuId" element={<MenuView />} />
              <Route path="/restaurant/:restaurantId/locations/:locationId/menus/:menuId/edit" element={<MenuForm />} />
              <Route path="/restaurant/:restaurantId/locations/:locationId/menus/:menuId/design" element={<MenuDesignFactory />} />

              {/* Purchase (per-location supplier purchasing): picker, per-location list, create form and read-only detail. */}
              <Route path="/restaurant/:restaurantId/purchase" element={<PurchaseLocationPicker />} />
              <Route path="/restaurant/:restaurantId/locations/:locationId/purchase" element={<PurchaseHome />} />
              <Route path="/restaurant/:restaurantId/locations/:locationId/purchase/new" element={<NewPurchase />} />
              <Route path="/restaurant/:restaurantId/locations/:locationId/purchase/:purchaseId" element={<PurchaseDetail />} />

              {/* Inventory is location-scoped: /inventory is a picker, the tracking lives under /locations/:locationId/inventory. */}
              <Route path="/restaurant/:restaurantId/inventory" element={<InventoryLocationPicker />} />
              <Route path="/restaurant/:restaurantId/locations/:locationId/inventory" element={<InventoryManagement />} />

              {/* Bar Inventory (liquor stock) is location-scoped too: picker, then the per-location stock screen. */}
              <Route path="/restaurant/:restaurantId/bar-inventory" element={<BarInventoryLocationPicker />} />
              <Route path="/restaurant/:restaurantId/locations/:locationId/bar-inventory" element={<BarInventoryManagement />} />

              {/* Liquor Management (per-location reports): picker, then the consumption / stock / top-selling screen. */}
              <Route path="/restaurant/:restaurantId/liquor-management" element={<LiquorManagementLocationPicker />} />
              <Route path="/restaurant/:restaurantId/locations/:locationId/liquor-management" element={<LiquorManagement />} />

              {/* Liquor Tracking (stocktake): picker, per-location home, the set editor, the count workspace and its report. */}
              <Route path="/restaurant/:restaurantId/liquor-tracking" element={<LiquorTrackingLocationPicker />} />
              <Route path="/restaurant/:restaurantId/locations/:locationId/liquor-tracking" element={<LiquorTrackingHome />} />
              <Route path="/restaurant/:restaurantId/locations/:locationId/liquor-tracking/sets" element={<LiquorSets />} />
              <Route path="/restaurant/:restaurantId/locations/:locationId/liquor-tracking/:stocktakeId" element={<StocktakeWorkspace />} />
              <Route path="/restaurant/:restaurantId/locations/:locationId/liquor-tracking/:stocktakeId/report" element={<StocktakeReport />} />

              {/* Back-compat: legacy flat menu deep-links land on the location picker instead of 404-ing. */}
              <Route path="/restaurant/:restaurantId/menus/new" element={<Navigate to=".." relative="path" replace />} />
              <Route path="/restaurant/:restaurantId/menus/:menuId" element={<Navigate to=".." relative="path" replace />} />
              <Route path="/restaurant/:restaurantId/menus/:menuId/edit" element={<Navigate to="../.." relative="path" replace />} />
              
              {/* Tables Management */}
              <Route path="/restaurant/:restaurantId/tables" element={<TablesViewPage />} />
              <Route path="/tables" element={<TablesViewPage />} />
              <Route path="/restaurant/:restaurantId/reservations" element={<ReservationPage />} />
              <Route path="/reservations" element={<ReservationPage />} />
              <Route path="/restaurant/:restaurantId/reservations/all" element={<AllReservations />} />
              <Route path="/reservations/all" element={<AllReservations />} />
              {/* The public booking link + QR. A door into BOOKING, used before
                  anybody arrives — so it sits with Reservations rather than with
                  the table and counter codes, which are doors into ordering. */}
              <Route path="/reservations/link" element={<ReservationLink />} />
              <Route path="/restaurant/:restaurantId/reservations/link" element={<ReservationLink />} />
              <Route path="/reservations/insights" element={<ReservationInsights />} />
              <Route path="/reservations/insights/:report" element={<ReservationInsights />} />
              {/* Old single-page report location → the new insights hub. */}
              <Route path="/reports/reservations" element={<Navigate to="/reservations/insights" replace />} />

              {/* QR Codes (admin): per-table, menu, kiosk and booking codes */}
              <Route path="/qr-codes" element={<QRCodes />} />
              <Route path="/restaurant/:restaurantId/qr-codes" element={<QRCodes />} />
              {/* Self Service Orders — the pre-payment switch per branch. */}
              {/* Kiosk analytics suite. The festival calendar is the module the
                  other reports filter by, so it lives inside Insights rather than
                  under the generic Reports section. */}
              <Route path="/self-service/insights"
                     element={<Suspense fallback={<ChartsLoading />}><InsightsHub /></Suspense>} />
              {/* The SAME calendar, reached from Settings as the module that
                  manages it. One screen, two doors — a second copy would be a
                  second holiday store to keep in step. */}
              <Route path="/settings/calendar"
                     element={<Suspense fallback={<ChartsLoading />}><FestivalCalendar /></Suspense>} />
              <Route path="/self-service/insights/festivals"
                     element={<Suspense fallback={<ChartsLoading />}><FestivalCalendar /></Suspense>} />
              {/* The reports still filling up. Each has its own menu entry, so
                  each needs somewhere to land: the question it will answer, and
                  an honest note that there is not enough history yet. A built
                  report takes its own route above this one. */}
              <Route path="/self-service/insights/:report"
                     element={<Suspense fallback={<ChartsLoading />}><InsightPlaceholder /></Suspense>} />

              {/* Customer directory (admin) */}
              <Route path="/customers" element={<CustomersPage />} />
              <Route path="/restaurant/:restaurantId/customers" element={<CustomersPage />} />

              {/* CRM — guest profiles from order history + growth reports */}
              <Route path="/crm" element={<CrmPage />} />
              {/* The Customer 360 — one guest, read whole. */}
              <Route path="/customers/:customerId" element={<CustomerProfilePage />} />
              <Route path="/restaurant/:restaurantId/customers/:customerId" element={<CustomerProfilePage />} />

              {/* Loyalty & Rewards — the programme's cockpit, members,
                  the reward catalogue and gift cards. Programme numbers
                  live under /settings/loyalty with the other settings. */}
              <Route path="/loyalty" element={<LoyaltyManager />} />
              <Route path="/loyalty/members" element={<MembersPage />} />
              <Route path="/loyalty/members/:accountId" element={<MemberProfile />} />
              <Route path="/loyalty/rewards" element={<RewardsCataloguePage />} />
              <Route path="/loyalty/gift-cards" element={<GiftCardsPage />} />
              <Route path="/settings/loyalty" element={<LoyaltyProgramSettings />} />

              {/* Staff Management and User Tracking */}
              <Route path="/staff-management" element={<StaffManagement />} />
              {/* Legacy alias: the old User Management screen merged into Staff Members, so keep the URL alive for bookmarks. */}
              <Route path="/user-management" element={<Navigate to="/staff-management" replace />} />
              <Route path="/roles-management" element={<RolesManagement />} />
              <Route path="/user-sessions" element={<UserSessions />} />
              <Route path="/staff/:staffId" element={<StaffDetail />} />
              <Route path="/staff/:staffId/sessions" element={<SessionDetails />} />
              <Route path="/staff-categories" element={<StaffCategories />} />
              {/* Settings */}
              <Route path="/settings/display" element={<SettingsDisplay />} />
              <Route path="/settings/company" element={<CompanyProfile />} />
              <Route path="/settings/organisation" element={<OrganisationSetup />} />
              <Route path="/settings/masters" element={<MastersManagement />} />
              <Route path="/settings/preferences" element={<AppPreferences />} />
              <Route path="/settings/menu-view" element={<MenuViewSettings />} />
              <Route path="/settings/locations" element={<LocationsManagement />} />
              {/* Restaurant groups: the franchise / brand layer between the company and its branches. */}
              <Route path="/settings/restaurant-groups" element={<RestaurantGroupsManagement />} />
              {/* Places (Near me) is a Website Settings section now; the old address lives on as a redirect. */}
              <Route path="/settings/finder-places" element={<Navigate to="/settings/website/places" replace />} />
              {/* Which kiosks a restaurant has, branch by branch, what each
                  branch plays while nobody is standing at them and how long a
                  panel waits before clearing an abandoned order. A Settings
                  screen; the old Self Service address lives on as a redirect. */}
              <Route path="/settings/kiosk" element={<KioskSettings />} />
              <Route path="/self-service/kiosk-settings" element={<Navigate to="/settings/kiosk" replace />} />
              <Route path="/settings/website" element={<WebsiteSettings />} />
              {/* Each section is its own address, so the sidebar can link them and a reload lands back on the same one. */}
              <Route path="/settings/website/:tab" element={<WebsiteSettings />} />
              {/* The old Website Photos page lives on as the Photos section. */}
              <Route path="/settings/website-photos" element={<Navigate to="/settings/website/photos" replace />} />
              <Route path="/settings/ingredients" element={<IngredientsManagement />} />
              <Route path="/settings/tax-rates" element={<TaxRatesManagement />} />
              <Route path="/settings/tax-groups" element={<TaxGroupsManagement />} />
              <Route path="/settings/tax-migration" element={<TaxMigrationPage />} />
              <Route path="/settings/reservations" element={<ReservationSettingsPage />} />
              {/* Notifications: the channels and the templates they carry. Each tab is
                  its own address, so the sidebar can link them and a reload lands back
                  on the same one; the bare path opens SMS. */}
              <Route path="/settings/notifications" element={<NotificationSettings />} />
              <Route path="/settings/notifications/:tab" element={<NotificationSettings />} />
              <Route path="/settings/realtime" element={<RealtimePage />} />
              {/* Registered Devices, gated by devices.view on the sidebar; the page itself gates the manage actions. */}
              <Route path="/settings/devices" element={<DevicesManagement />} />
              {/* One terminal's staff-shift ledger as a full page: per-staff totals and a day-by-day list. */}
              <Route path="/settings/devices/:deviceId/shifts" element={<DeviceShiftsReport />} />
            </Routes>
            </ErrorBoundary>
            </div>
            <Toaster />
            <ConfirmDialogHost />
            {/* Toasts staff when a new guest reservation request needs approval. */}
            <ReservationRequestsWatcher />
          </div>
          <DodoHelp />
        </div>
        </div>
      </AppSettingsProvider>
    </RestaurantProvider>
  );
}

// Main App component with authentication check
/** Placeholder while a lazily-loaded analytics chunk arrives. */
function ChartsLoading() {
  return (
    <div className="min-h-screen bg-[#FAF7F2] p-6">
      <div className="h-8 w-48 animate-pulse rounded bg-[#EFE7DC]" />
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-xl bg-[#EFE7DC]" />
        ))}
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <ThemeProvider>
          <LanguageProvider>
            <AppContent />
          </LanguageProvider>
        </ThemeProvider>
      </AuthProvider>
    </Router>
  );
}

// App content that shows login or authenticated app
function AppContent() {
  const location = useLocation();
  const {
    isAuthenticated,
    isLoading,
    login,
    showIntro,
    dismissIntro,
    needsOnboarding,
    completeOnboarding,
    cancelOnboarding,
    session,
    user,
  } = useAuth();
  const { t } = useT();

  // Welcome tips toast: on for a freshly-authenticated session, off when dismissed; driven by AuthContext.showIntro.
  const [showWelcomeTips, setShowWelcomeTips] = useState(false);

  // On "intro time" play the chime, show the tips and clear the flag immediately - no full-screen animation.
  useEffect(() => {
    if (showIntro) {
      playLoginChime();
      setShowWelcomeTips(true);
      dismissIntro();
    }
  }, [showIntro, dismissIntro]);

  // Public customer routes bypass the staff auth gate, declared after the hooks so hook order stays stable.
  if (location.pathname.startsWith('/customer/')) {
    return (
      <>
        <Routes>
          <Route path="/customer/login" element={<CustomerLogin />} />
          <Route path="/customer/account-login" element={<CustomerAccountLogin />} />
          <Route path="/customer/menu" element={<CustomerMenu />} />
          {/* The self-order PANEL. Its own screen rather than a wide variant
              of the guest menu: standing, anonymous, one session at a time,
              and thrown away after sixty idle seconds. */}
          <Route path="/customer/kiosk" element={<KioskOrder />} />
          <Route path="/customer/reserve" element={<CustomerReserve />} />
        </Routes>
        <ConfirmDialogHost />
      </>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // restoboxx.com names no restaurant: no login, no SSO, no reset there
    // (the server refuses them too) - RestoBOXX's own landing page instead.
    if (isBareDomain()) {
      return <Suspense fallback={null}><RestoLanding /></Suspense>;
    }
    // /sso/landing must render before the unauth gate, and /auth/sso-handoff is the URL SaaSBOXX advertises.
    if (typeof window !== 'undefined') {
      const p = window.location.pathname;
      if (p === '/sso/landing' || p === '/auth/sso-handoff') {
        return <SsoLanding />;
      }
      // Public self-service password reset (mock OTP), reachable from the login page without a session.
      if (p === '/forgot-password') {
        return <ForgotPassword />;
      }
    }
    return <ModernLogin onLogin={login} />;
  }

  if (needsOnboarding) {
    return (
      <FirstTimeOnboarding
        demoToken={session?.token || localStorage.getItem('sessionToken')}
        onComplete={completeOnboarding}
        onCancel={cancelOnboarding}
      />
    );
  }

  return (
    <>
      <AuthenticatedApp />
      {showWelcomeTips && (
        <WelcomeTipsToast user={user} onClose={() => setShowWelcomeTips(false)} />
      )}
    </>
  );
}

export default App;
